import { resolveFeatureFlag } from "@/analytics/featureFlags";
import {
  DEFAULT_POPUP_VARIANT,
  POPUP_OFFER_FLAG_KEY,
  isPopupVariant,
  type PopupVariant,
} from "@/utils/popupOffers";

/** `?popup_variant=c` previews a variant; it sticks for the rest of the session. */
const OVERRIDE_PARAM = "popup_variant";
const OVERRIDE_SESSION_KEY = "popupVariantOverride";
const SESSION_STARTED_KEY = "popupSessionStartedAt";

const toVariant = (value: unknown): PopupVariant | null => {
  if (typeof value !== "string") return null;
  const upper = value.toUpperCase();
  return isPopupVariant(upper) ? upper : null;
};

const readOverride = (): PopupVariant | null => {
  try {
    const fromQuery = toVariant(
      new URLSearchParams(window.location.search).get(OVERRIDE_PARAM),
    );
    if (fromQuery) {
      sessionStorage.setItem(OVERRIDE_SESSION_KEY, fromQuery);
      return fromQuery;
    }
    return toVariant(sessionStorage.getItem(OVERRIDE_SESSION_KEY));
  } catch (error) {
    console.error("Failed to read the popup variant override:", error);
    return null;
  }
};

/**
 * The visitor's popup variant. Reading the flag fires PostHog's exposure event,
 * so this is only called once a visitor is eligible to see the popup — that
 * makes "exposed" mean "could have seen it", which is the right denominator for
 * comparing variants that open at different times. A flag that never loads, or
 * a value outside A–E (such as "control"), falls back to today's popup.
 */
export const resolvePopupVariant = async (
  signal: AbortSignal,
  timeoutMs = 1500,
): Promise<PopupVariant> => {
  const override = readOverride();
  if (override) return override;
  const value = await resolveFeatureFlag(
    POPUP_OFFER_FLAG_KEY,
    signal,
    timeoutMs,
  );
  return toVariant(value) ?? DEFAULT_POPUP_VARIANT;
};

/** When this visit started, so a timed trigger counts time on site, not per page. */
export const sessionStartedAt = (): number => {
  const now = Date.now();
  try {
    const stored = Number(sessionStorage.getItem(SESSION_STARTED_KEY));
    if (stored > 0 && stored <= now) return stored;
    sessionStorage.setItem(SESSION_STARTED_KEY, String(now));
  } catch (error) {
    console.error("Failed to read the popup session start:", error);
  }
  return now;
};
